<template lang="pug">
  form#user-form(
    name="user-form"
    @submit.prevent="submit"
    novalidate
  )
    .modal-card
      header.modal-card-head
        p.modal-card-title(v-once) {{ title }}
      section.modal-card-body.modal-tabs
        b-tabs(
          v-model="activeTab"
          :animated="false"
          position="is-centered"
          expanded
        )
          b-tab-item(label="Profile")
            b-field(grouped)
              b-field(
                label="First Name"
                :message="errors.has('first_name') ? errors.first('first_name') : ''"
                :type="errors.has('first_name') ? 'is-danger' : ''"
                expanded
              )
                b-input(
                  name="first_name"
                  data-vv-as="first name"
                  data-vv-delay="1000"
                  v-validate="'required'"
                  :value="user.first_name"
                  @input="set($event, 'first_name')"
                )
              b-field(
                label="Last Name"
                :message="errors.has('last_name') ? errors.first('last_name') : ''"
                :type="errors.has('last_name') ? 'is-danger' : ''"
                expanded
              )
                b-input(
                  name="last_name"
                  data-vv-as="last name"
                  data-vv-delay="1000"
                  v-validate="'required'"
                  :value="user.last_name"
                  @input="set($event, 'last_name')"
                )
            b-field(
              label="Email Address"
              :message="errors.has('email') ? errors.first('email') : ''"
              :type="errors.has('email') ? 'is-danger' : ''"
            )
              b-input(
                type="email"
                name="email"
                data-vv-delay="1000"
                v-validate="{ required: true, email: true, unique: { id: user.id, resource: 'users', fieldName: 'email' } }"
                :value="user.email"
                @input="set($event, 'email')"
              )
            b-field(
              label="Password"
              :message="errors.has('password') ? errors.first('password') : ''"
              :type="errors.has('password') ? 'is-danger' : ''"
              v-if="isNew"
            )
              b-input(
                name="password"
                type="text"
                autocomplete="new-password"
                data-vv-delay="1000"
                v-validate="'required|password'"
                :value="user.password"
                @input="set($event, 'password')"
              )
          b-tab-item(label="Permissions")
            b-field
              b-switch(
                :value="user.is_active"
                @input="set($event, 'is_active')"
              ) {{ user.is_active ? 'Active' : 'Inactive' }}
            .b-table(v-if="user.is_active")
              table.table
                thead
                  tr
                    th Access
                    th Module
                    th Description
                tbody
                  tr
                    td
                      b-checkbox(
                        :value="user.access.account"
                        @input="set($event, 'access.account')"
                      )
                    td
                      .module
                        b-icon(icon="settings")
                        span Account
                    td
                      small Global account settings
                  tr
                    td
                      b-checkbox(
                        :value="user.access.media"
                        @input="set($event, 'access.media')"
                      )
                    td
                      .module
                        b-icon(icon="podcast")
                        span Media
                    td
                      small Audio/video assets
                  tr
                    td
                      b-checkbox(
                        :value="user.access.events"
                        @input="set($event, 'access.events')"
                      )
                    td
                      .module
                        b-icon(icon="calendar-multiple")
                        span Events
                    td
                      small Events and sign-up forms
                  tr
                    td
                      b-checkbox(
                        :value="user.access.users"
                        @input="set($event, 'access.users')"
                      )
                    td
                      .module
                        b-icon(icon="account-check")
                        span Users
                    td
                      small Control panel access
                  tr
                    td
                      b-checkbox(
                        :value="user.access.church"
                        @input="set($event, 'access.church')"
                      )
                    td
                      .module
                        b-icon(icon="church")
                        span Church
                    td
                      small Ministry details and service times
                  tr
                    td
                      b-checkbox(
                        :value="user.access.articles"
                        @input="set($event, 'access.articles')"
                      )
                    td
                      .module
                        b-icon(icon="fountain-pen-tip")
                        span Articles
                    td
                      small Blog posts
            b-notification(v-else :closable="false") No control panel access
      footer.modal-card-foot
        button.button.is-success(
          type="submit"
          :class="{ 'is-loading': settings.submitting }"
        ) Save User
</template>

<script>
import { mapGetters } from 'vuex';
import * as actions from '@/store/types/actions';
import * as getters from '@/store/types/getters';
import * as mutations from '@/store/types/mutations';

export default {
  name: 'user-form',
  computed: {
    ...mapGetters({
      authenticated: getters.GET_AUTHENTICATED_USER,
      user: getters.GET_USER,
    }),
    isNew() {
      return this.user.id === undefined;
    },
    title() {
      return this.isNew ? 'New User' : `Edit User: ${this.user.full_name}`;
    },
  },
  data() {
    return {
      activeTab: 0,
      settings: {
        submitting: false,
      },
    };
  },
  methods: {
    set(val, key) {
      this.$store.commit(mutations.SET_USER_FIELD, {
        key,
        val,
      });
    },
    async submit() {
      const valid = await this.$validator.validateAll();
      if (valid) {
        this.settings.submitting = true;
        const action = this.isNew ? actions.DO_CREATE_USER : actions.DO_UPDATE_USER;
        const payload = {
          id: this.user.id,
          access_account: this.user.access.account,
          access_articles: this.user.access.articles,
          access_church: this.user.access.church,
          access_events: this.user.access.events,
          access_media: this.user.access.media,
          access_users: this.user.access.users,
          first_name: this.user.first_name,
          is_active: this.user.is_active,
          last_name: this.user.last_name,
          email: this.user.email,
          password: (this.isNew ? this.user.password : null),
        };
        try {
          const response = await this.$store.dispatch(action, payload);
          this.settings.submitting = false;
          this.$parent.close();
          this.$store.commit(mutations.SET_USERS_REFRESH, true);
          this.$store.commit(mutations.SET_APP_SNACKBAR, {
            type: 'is-success',
            message: response.message,
          });
          if (this.authenticated.id === this.user.id) {
            this.$store.commit(mutations.SET_AUTHENTICATED_USER, response.data.users[0]);
          }
        } catch (error) {
          this.settings.submitting = false;
          this.$dialog.alert({
            title: 'Oops...',
            message: error.response.data.data.error_message,
            type: 'is-danger',
            hasIcon: true,
          });
        }
      }
    },
  },
  props: [
    'edit',
  ],
};
</script>

<style lang="scss">
#user-form {
  .module {
    display: flex;
    align-items: center;
    .icon {
      margin-right: 0.35rem;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/form/UserForm.vue